import React, { useEffect } from 'react';
import Address from 'domain/Beneficiaries/CreateBeneficiary/Form/PersonalDetails/Address';
import { FormikProps } from 'formik';
import { components } from 'generated/payment-initialisation/schema';
import { TCountries } from 'models/countries';
import t from 'utils/translationHelper';

import { Input } from '@alpha/ui-lib/ui/Input/Input';
import { APTooltip } from '@alpha/ui-lib/ui/Tooltip';
import { Typography } from '@alpha/ui-lib/ui/Typography';
import { faExclamationCircle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { IRemitterFormValues } from './RemitterDrawer';
import useStyles from './RemitterDrawer.styles';

interface IBankField {
  label: string;
  required: boolean;
  name: keyof IRemitterFormValues;
  apiName: string;
}

interface IBankDetailsSectionProps {
  form: FormikProps<IRemitterFormValues>;
  editRemitterValues?: components['schemas']['RemitterResponse'];
  countries: TCountries;
}

const BankDetailsSection: React.FC<IBankDetailsSectionProps> = ({ form, editRemitterValues, countries }) => {
  const styles = useStyles();

  const bankFields: IBankField[] = [
    {
      label: t('bank_name'), name: 'bankName', apiName: 'BankName', required: true,
    },
    {
      label: t('national_bank_code'), name: 'nationalBankCode', apiName: 'NationalBankCode', required: false,
    },
    {
      label: t('account_number'), name: 'accountNumber', apiName: 'AccountNumber', required: false,
    },
    {
      label: t('iban'), name: 'iban', apiName: 'Iban', required: false,
    },
    {
      label: t('swift/bic'), name: 'swiftBic', apiName: 'Bic', required: true,
    },
  ];

  return (
    <div style={{ border: '1px solid #E4E4E7', borderRadius: '8px', padding: '16px' }}>
      <div style={{ fontWeight: 600, marginBottom: '16px' }}>{t('bank_details')}</div>
      {bankFields.map((field) => (
        <div key={field.name} style={{ marginBottom: '16px' }}>
          <Typography
            variant="subtitle1"
            style={{
              marginBottom: '6px',
              color: editRemitterValues?.Fields?.[field.apiName]?.Editable
                 && (form.values[field.name] === '' || !form.values[field.name]) ? '#CF752F' : 'unset',
            }}
          >
            {(editRemitterValues?.Fields?.[field.apiName]?.Editable
                 && (form.values[field.name] === '' || !form.values[field.name]))
              ? (<FontAwesomeIcon icon={faExclamationCircle} style={{ color: '#CF752F', marginRight: '4px' }} />) : null}
            {field.label}
            {field.required ? '*' : ''}
          </Typography>
          {(() => {
            const isNotEditable = editRemitterValues?.Fields?.[field.apiName]?.Editable === false;
            const inputElement = (
              <Input
                fullWidth
                variant="filled"
                placeholder={field.label}
                testId={field.name}
                name={field.name}
                onChange={form.handleChange}
                onBlur={form.handleBlur}
                value={form.values[field.name]}
                type="text"
                className={styles.textInput}
                error={form.touched[field.name] && Boolean(form.errors[field.name])}
                helperText={form.touched[field.name] && form.errors[field.name]}
                disabled={isNotEditable}
              />
            );

            return isNotEditable ? (
              <APTooltip title={t('you_are_unable_to_edit_this_information')} arrow>
                <div>{inputElement}</div>
              </APTooltip>
            ) : (
              inputElement
            );
          })()}
        </div>
      ))}
      <div>
        {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
        <Address
          type="bank"
          form={form as FormikProps<any>}
          countries={countries}
          editingDraft={!!editRemitterValues}
          disabled={false}
          bankWithCountry
          requiresAddress
        />
      </div>
    </div>
  );
};

export default BankDetailsSection;
